import { z } from "zod";
/** The catalogue read (S5 · 1.3). One shape, shared by services/api and the CRM,
 *  so a field cannot be renamed on one side of the wire and missed on the other.
 *
 *  Image fields are RELATIVE paths exactly as `website.*` stores them; the face
 *  that displays them prefixes the blob base. */
export const CatalogueColour = z.object({
    name: z.string(),
    swatch: z.string().nullable(),
    image: z.string().nullable(),
    /** The supplier sells this colour only to builders or on request (26 Oct 2026): orderable, not on the website, not offered by default. */
    specialOrder: z.boolean().optional(),
});
/** One specification of a product, from the website's typed spec table for its family. `value` null = not recorded. */
export const CatalogueSpec = z.object({
    key: z.string(),
    value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
});
export const CatalogueCard = z.object({
    id: z.string(),
    name: z.string(),
    /** the customer-facing name from the website, falling back to the catalogue name */
    customerName: z.string(),
    slug: z.string().nullable(),
    supplier: z.string(),
    /** The brand the customer knows. PACO supplies both PACO and National Select ranges (one ABN, two
     *  brands), so `supplier` is not the brand. Optional: an older read falls back to `supplier`. */
    brand: z.string().optional(),
    type: z.string(),
    subType: z.string(),
    uom: z.string().nullable(),
    /** roll width in metres for carpet, plank/tile width in mm for hard floor */
    width: z.number().nullable(),
    pattern: z.string().nullable(),
    hero: z.string().nullable(),
    blurb: z.string().nullable(),
    benefit: z.string().nullable(),
    residential: z.boolean(),
    commercial: z.boolean(),
    featured: z.boolean(),
    website: z.boolean(),
    colours: z.array(CatalogueColour),
    colourCount: z.number(),
    /** cost per `unit`, resolved through the effective-dated history */
    costFrom: z.number().nullable(),
    costTo: z.number().nullable(),
    unit: z.string(),
    basis: z.string().nullable(),
    effectiveDate: z.string().nullable(),
    issues: z.array(z.string()),
    rule: z.string().nullable(),
    docs: z.number(),
    /** Public supplier PDFs only. Private price evidence never becomes a download link. */
    documentLinks: z.array(z.object({ title: z.string(), docType: z.string(), url: z.string().url() })).optional(),
    /** Every field its family records, in a fixed order, recorded or not (25 Oct 2026). Absent for non-floor products. */
    specFamily: z.string().nullable().optional(),
    specs: z.array(CatalogueSpec).optional(),
});
export const CatalogueView = z.object({
    /** the Sydney day the costs were resolved against */
    asOf: z.string(),
    /** variants priced from `catalog.*` rather than the legacy catalogue column */
    historyUsed: z.number(),
    products: z.array(CatalogueCard),
});
const M2_UOM = /^(m²|m2|sqm|m\^2)$/i;
/** Why a product cannot be priced on the website, or null when it can. */
export function listingRefusal(p) {
    if (p.sell_price == null || !(p.sell_price > 0))
        return `'${p.name}' has no sell price`;
    if (p.uom != null && !M2_UOM.test(p.uom.trim()))
        return `'${p.name}' is not priced per m² (${p.uom})`;
    if (p.cost != null && p.sell_price <= p.cost)
        return `'${p.name}' sells at or below its cost — fix the catalogue price first`;
    return null;
}
/** Standard installation cost per m² of floor for one engine installation row (Jacky, 9 Oct 2026: each
 *  Price my floor line carries its installation cost so the list price is compared with a GM). Labour,
 *  glue when the method glues, underlay when the row carries it. Carpet labour is per lineal metre of a
 *  roll, so per m² is labour ÷ roll width; underlay is already per m² of floor. A per-LM row with no roll
 *  width cannot be stated per m² → null, never a guessed width. */
export function installCostPerM2(row, width) {
    if (!row || row.labour == null)
        return null;
    const glue = row.glued ? row.glue ?? 0 : 0, underlay = row.underlay ?? 0;
    const r2 = (n) => Math.round(n * 100) / 100;
    if (row.unit === 'm²')
        return r2(row.labour + glue + underlay);
    if (width == null || !(width > 0))
        return null;
    return r2((row.labour + glue) / width + underlay);
}
/** Categories whose list price is SUPPLY ONLY, with installation priced separately per method (Jacky,
 *  10 Oct 2026: "for engineer timber, different installation is priced separately" — the quotes agree:
 *  J-0826-10197 herringbone board $65/m² supply + "Direct Glued-Down" $40/m² + glue $13/m²). Every other
 *  category's list price includes its standard installation. */
export const SEPARATE_INSTALL_SUBTYPES = ["Engineered Timber"];
/** Price my floor's ESTIMATOR (Jacky, 25 Oct 2026): a hard floor's supplied & installed price includes its installation,
 *  underlay (floating methods only) and scotia, at each manufacturer's own prices, on the floor m² with no cut waste (the
 *  customer pays waste). Trims and moisture barrier are optional, not included. The prices live in the
 *  `price_my_floor_inclusions` config; this is only the arithmetic, shared by the page, EmmA's reads and the public list. */
/** Floors whose price includes scotia (and underlay when floating). Vinyl plank is not one: Jacky, 26 Oct 2026, "Vinyl
 *  planks have no Scotia" — its price includes installation only. */
export const HARD_FLOOR_SUBTYPES = ["Laminate", "Hybrid", "Bamboo", "Engineered Timber", "Solid Timber"];
/** Installation methods that float on an underlay (the rest are glued or fixed). */
export const FLOATING_INSTALLS = ["laminate", "hybrid", "bamboo", "engineered"];
export function includedCost(cfg, supplier, category, installId) {
    const r2 = (n) => Math.round(n * 100) / 100;
    if (!category || !HARD_FLOOR_SUBTYPES.includes(category))
        return { applies: false, underlay_m2: 0, scotia_m2: 0, cost_m2: 0, missing: [], included: ["Installation"] };
    const floating = !!installId && FLOATING_INSTALLS.includes(installId);
    const row = cfg?.rows.find((r) => r.supplier.trim().toLowerCase() === (supplier ?? "").trim().toLowerCase() && r.category === category);
    const underlay = floating ? (row?.underlay_m2 ?? null) : 0;
    const scotia = row?.scotia_lm != null ? r2(row.scotia_lm * (cfg?.scotia_lm_per_m2 ?? 1)) : null;
    const missing = [...(underlay == null ? ["underlay"] : []), ...(scotia == null ? ["scotia"] : [])];
    return { applies: true, underlay_m2: underlay, scotia_m2: scotia, cost_m2: missing.length ? null : r2((underlay ?? 0) + (scotia ?? 0)),
        missing, included: ["Installation", ...(floating ? ["Underlay"] : []), "Scotia"] };
}
/** Engineered timber is sold supply + the installation method (Jacky: "Supply + method"), listed as one supplied &
 *  installed price per method: a plank floats or is direct glued; herringbone and chevron are glued only. */
export const timberMethods = (engineRule) => engineRule === "engineered" ? ["engineered", "engineeredGlue"] : engineRule ? [engineRule] : [];
/** The installation COST Price my floor uses for a category the Proposal engine prices by hand (Jacky, 25 Oct 2026:
 *  sheet vinyl at "$25 + $10 glue = $35"). The engine's `installationRule` returns null for Sheet Vinyl on purpose —
 *  a Proposal still prices it at the measure — so this applies to the Price my floor GM and EmmA's reads only, and the
 *  figure itself is the `proposal_pricing.installation` row named here (configuration, not code). */
export const PRICE_MY_FLOOR_INSTALL_FOR_MANUAL = { "Sheet Vinyl": "sheetVinyl" };
export const priceMyFloorInstallRule = (engineRule, subType) => engineRule ?? (subType ? PRICE_MY_FLOOR_INSTALL_FOR_MANUAL[subType] ?? null : null);
export const installBasis = (subType) => subType && SEPARATE_INSTALL_SUBTYPES.includes(subType) ? "supply" : "installed";
/** The list price that gives a gross margin over a cost, rounded UP to the whole dollar (Jacky: "price
 *  changes (cell up to whole $)"). Rounding up means the margin achieved is never below the one asked for.
 *  null when the margin is not a fraction below 1 or the cost is unknown. */
export function priceForMargin(cost, gm) {
    if (cost == null || !Number.isFinite(cost) || cost < 0 || !Number.isFinite(gm) || gm >= 1 || gm <= -1)
        return null;
    return Math.ceil(Math.round((cost / (1 - gm)) * 1e6) / 1e6);
}
const F = (key, label, unit) => (unit ? { key, label, unit } : { key, label });
const thick = F("thickness_mm", "Thickness", "mm"), wear = F("wear_layer_mm", "Wear layer", "mm"), len = F("length_mm", "Board length", "mm"), wid = F("width_mm", "Board width", "mm");
const install = F("install_methods", "Installation"), flat = F("subfloor_flatness", "Subfloor flatness"), pattern = F("pattern", "Pattern");
const warrantyRes = F("warranty_residential_yrs", "Residential warranty", "years"), warrantyCom = F("warranty_commercial_yrs", "Commercial warranty", "years");
const species = F("species", "Species"), grade = F("grade", "Grade"), finish = F("finish", "Finish"), edge = F("edge_profile", "Edge profile");
const slip = F("slip_rating", "Slip rating"), rollWidth = F("roll_width_m", "Roll width", "m"), antimicrobial = F("antimicrobial", "Antimicrobial");
const fibre = F("fibre", "Fibre"), pile = F("pile_type", "Pile type"), pileWeight = F("pile_weight_gsm", "Pile weight", "g/m²"), backing = F("backing_type", "Backing");
export const CATALOGUE_SPEC_FIELDS = {
    laminate: [thick, len, wid, F("ac_rating", "AC rating"), F("water_resistance", "Water resistance"), edge, install, warrantyRes, flat],
    hybrid: [thick, wear, len, wid, pattern, edge, F("underlay_attached", "Underlay attached"), install, warrantyRes, flat],
    engineered_timber: [species, grade, finish, thick, wear, len, wid, pattern, install, warrantyRes, flat],
    solid_timber: [species, grade, finish, thick, len, wid, pattern, install, flat],
    vinyl_plank: [thick, wear, len, wid, slip, install, warrantyRes, warrantyCom, flat],
    // A vinyl TILE (Excelon VCT, EarthCuts; 25 Oct 2026) records the same facts as a plank, read from the same table: the piece is a tile, so its sides are not "board" sides.
    vinyl_tile: [thick, wear, F("length_mm", "Tile length", "mm"), F("width_mm", "Tile width", "mm"), slip, install, warrantyRes, warrantyCom, flat],
    vinyl_sheet: [thick, wear, rollWidth, slip, F("welded_seams", "Welded seams"), antimicrobial, warrantyRes, warrantyCom, flat],
    carpet: [fibre, F("fibre_branded_name", "Branded fibre"), pile, pileWeight, F("total_weight_gsm", "Total weight", "g/m²"), rollWidth, backing, F("stain_treatment", "Stain treatment"), F("wear_rating", "Wear rating"), warrantyRes],
    carpet_tile: [F("tile_size_cm", "Tile size", "cm"), fibre, pile, pileWeight, backing, F("install_method", "Installation"), F("light_reflectance", "Light reflectance"), antimicrobial, warrantyCom],
};
/** The `website` table that holds a family's facts. Vinyl tile shares the vinyl plank table (same columns: thickness, wear layer,
 *  length, width, slip, installation, warranties) — a ninth table for the same nine columns would only split the fill. */
export const SPEC_TABLE = {
    laminate: "specs_laminate", hybrid: "specs_hybrid", engineered_timber: "specs_engineered_timber", solid_timber: "specs_solid_timber",
    vinyl_plank: "specs_vinyl_plank", vinyl_tile: "specs_vinyl_plank", vinyl_sheet: "specs_vinyl_sheet", carpet: "specs_carpet", carpet_tile: "specs_carpet_tile",
};
/** The spec table a product's facts live in — the same mapping the Proposal profiles use (packages/commands/src/proposal-catalogue.ts). */
export function specFamily(type, subType) {
    switch (subType) {
        case "Laminate": return "laminate";
        case "Hybrid": return "hybrid";
        case "Engineered Timber": return "engineered_timber";
        case "Solid Timber": return "solid_timber";
        case "Vinyl Plank": return "vinyl_plank";
        case "Vinyl Tile": return "vinyl_tile";
        case "Sheet Vinyl": return "vinyl_sheet";
        case "Carpet Tile": return "carpet_tile";
    }
    return type === "Carpet" ? "carpet" : null;
}
const specValue = (v) => {
    if (v === null || v === undefined)
        return null;
    if (typeof v === "boolean")
        return v;
    if (typeof v === "number")
        return Number.isFinite(v) ? v : null;
    if (Array.isArray(v)) {
        const t = v.map((x) => String(x ?? "").trim()).filter(Boolean).join(", ");
        return t || null;
    }
    const t = String(v).trim();
    if (!t)
        return null;
    // PostgREST returns numeric columns as strings: keep them numbers so the face can add the unit.
    return /^-?\d+(\.\d+)?$/.test(t) ? Number(t) : t;
};
/** Every field of a family, from one spec-table row (or none). Flatness reads as "3 mm / 3 m" from its two columns. */
export function catalogueSpecs(family, row) {
    return CATALOGUE_SPEC_FIELDS[family].map((f) => {
        if (f.key === "subfloor_flatness") {
            const mm = specValue(row?.subfloor_flatness_mm), over = specValue(row?.subfloor_flatness_over_m);
            return { key: f.key, value: typeof mm === "number" ? (typeof over === "number" ? `${mm} mm / ${over} m` : `${mm} mm`) : null };
        }
        const v = specValue(row?.[f.key]);
        // The table stores the abrasion class as a number; the trade writes it "AC5".
        return { key: f.key, value: f.key === "ac_rating" && typeof v === "number" ? `AC${v}` : v };
    });
}
/** The value in words, English — EmmA's read and anything that is not a translated screen. */
export function specText(field, value) {
    if (value === null)
        return null;
    if (typeof value === "boolean")
        return value ? "Yes" : "No";
    return field.unit && typeof value === "number" ? `${value} ${field.unit}` : String(value);
}
//# sourceMappingURL=catalogue.js.map